/**
 * The portfolio pilot timeline / Gantt (F16).
 *
 * Every pilot laid on one shared time axis, its bar positioned and sized by the
 * percentages the API computed. Three rules from PRD §9.7 and §4.2 shape it:
 *
 *   - **Colour is the pilot's status, a datum — never "how good".** A concluded
 *     pilot is not "success" and an aborted one is not "error" (PRD §5.4), so
 *     the bar uses the status ramp, not the semantic palette. The status name
 *     and the dates sit in text beside every bar, so the colour is never the
 *     only signal.
 *   - **A pilot with no start cannot be drawn**, so those are listed below the
 *     chart as "unscheduled" rather than dropped or faked onto the axis.
 *   - **The chart is a labelled list, not an image.** Each row is a real DOM
 *     element a screen reader can read as "title, status, start to end", so the
 *     timeline is legible without seeing the bars.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.2, PRD F16, §4.2, §9.7
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { PilotTimeline } from '@nexus/contracts';

import { PILOT_STATUS_COLOR } from '../../components/domain/pilot-status';
import { PilotStatusBadge } from '../../components/domain/PilotStatusBadge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { formatDate } from '../../lib/format';
import { pilotPath, ROUTES } from '../../lib/routes';

import { usePilotTimeline } from './api/pilots.queries';

export function PilotTimelinePage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = usePilotTimeline();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('pilot.timeline.title')}
        lead={t('pilot.timeline.lead')}
        actions={
          <Link to={ROUTES.pilots} className="text-blue text-sm hover:underline">
            {t('pilot.timeline.backToList')}
          </Link>
        }
      />

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <TimelineContent timeline={data} />
      )}
    </>
  );
}

function TimelineContent({ timeline }: { timeline: PilotTimeline }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-s4">
      <Card>
        <CardHead>
          <Kicker as="h2">{t('pilot.timeline.chartTitle')}</Kicker>
        </CardHead>
        <CardBody>
          {timeline.bars.length === 0 ? (
            <Empty
              title={t('pilot.timeline.emptyTitle')}
              action={
                <Link to={ROUTES.pilots} className="text-blue text-sm hover:underline">
                  {t('pilot.timeline.backToList')}
                </Link>
              }
            >
              {t('pilot.timeline.emptyBody')}
            </Empty>
          ) : (
            <>
              {timeline.windowStart !== null && timeline.windowEnd !== null && (
                <div className="text-body mb-s3 flex justify-between text-xs font-light">
                  <span>{formatDate(timeline.windowStart)}</span>
                  <span>{formatDate(timeline.windowEnd)}</span>
                </div>
              )}
              <ul className="flex flex-col gap-2.5">
                {timeline.bars.map((bar) => (
                  <li key={bar.id} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to={pilotPath(bar.id)}
                        className="text-navy-text truncate text-sm font-light hover:underline"
                      >
                        {bar.title}
                        <span className="text-body ml-2 text-xs">{bar.startupName}</span>
                      </Link>
                      <PilotStatusBadge status={bar.status} />
                    </div>
                    {/* The track holds the bar; the bar is positioned and sized
                        by the API's percentages. `aria-hidden` because the row's
                        text already states the span. */}
                    <div
                      className="bg-off-white relative h-4 w-full overflow-hidden rounded-full"
                      aria-hidden="true"
                    >
                      <span
                        className="absolute inset-y-0 rounded-full"
                        style={{
                          left: `${String(bar.offsetPct)}%`,
                          width: `${String(bar.widthPct)}%`,
                          backgroundColor: PILOT_STATUS_COLOR[bar.status],
                        }}
                      />
                    </div>
                    <p className="text-body text-xs font-light">
                      {t('pilot.timeline.span', {
                        start: formatDate(bar.startDate),
                        end: bar.openEnded
                          ? t('pilot.timeline.ongoing')
                          : formatDate(bar.endDate),
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      {timeline.unscheduled.length > 0 && (
        <Card>
          <CardHead>
            <Kicker as="h2">{t('pilot.timeline.unscheduledTitle')}</Kicker>
          </CardHead>
          <CardBody>
            <ul className="flex flex-col divide-y divide-gray-100">
              {timeline.unscheduled.map((pilot) => (
                <li key={pilot.id} className="flex items-center justify-between py-2">
                  <Link
                    to={pilotPath(pilot.id)}
                    className="text-navy-text text-sm font-light hover:underline"
                  >
                    {pilot.title}
                    <span className="text-body ml-2 text-xs">{pilot.startupName}</span>
                  </Link>
                  <PilotStatusBadge status={pilot.status} />
                </li>
              ))}
            </ul>
            <Note className="mt-s3">{t('pilot.timeline.unscheduledNote')}</Note>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
